import "./NewOrder.scss";
import { Header, Title, Subtitle, Text, Subheader } from "../../../Components/Typography";
import { FileUpload } from "../../../Components/FileUpload";
import { Input } from "../../../Components/Input";
import { LogoSection } from "../../../Components/LogoSection";
import { OrderSummary } from "../../../Components/OrderSummary";
import useResponsive from "../../../hooks/useResponsive";
import {
  FileUploadContext,
  FileUploadContextProps,
} from "../../../Contexts/FileUploadContext";
import { useContext, useEffect } from "react";
import {
  OrderContext,
  OrderContextProps,
} from "../../../Contexts/OrderContext";
import { routes } from "../../../Router";
import { useNavigate } from "react-router-dom";
import useLocalStorage from "../../../hooks/useLocalStorage";
import { LoadingCards } from "../../../Components/Loading";
import { Dropdown } from "../../../Components/Dropdown";
import {
  EntitiesContext,
  EntitiesContextProps,
} from "../../../Contexts/EntitiesContext";
import Option from "../../../types/Option";

export const NewOrder = () => {
  const navigate = useNavigate();
  const { isDesktopOrLaptop } = useResponsive();
  const { getSessionStorageItem, setSessionStorageItem, clearSessionStorage } = useLocalStorage();

  const { loading, getEntities, entitiesOptions }: EntitiesContextProps =
    useContext<any>(EntitiesContext);

  const {
    sheetType,
    sheetSize,
    color,
    ringType,
    side,
    copies,
    getCalculatedShippingCost,
    getCalculatedPrice,
    totalPrice,
  }: OrderContextProps = useContext<any>(OrderContext);

  const { fileUpload }: FileUploadContextProps =
    useContext<any>(FileUploadContext);
  const { additionalNotes, setAdditionalNotes } = fileUpload;

  // Detectar si es una pestaña duplicada y redirigir al inicio
  const handleTabDuplication = () => {
    const existingTrackingCode = getSessionStorageItem("el-rayo-random-id");
    
    // Si ya hay un tracking code, significa que es una pestaña duplicada
    if (existingTrackingCode) {
      // Redirigir a la página principal para evitar conflictos
      window.location.href = '/';
    }
  };

  const isStep1Valid =
    fileUpload.uploadedPages > 0 &&
    !!sheetSize?.selectedSheetSize &&
    !!color?.selectedColor &&
    !!side?.selectedSide &&
    !!sheetType?.selectedSheetType &&
    !!ringType?.selectedRingType &&
    copies.numberOfCopies > 0 &&
    !!totalPrice;

  const onClickNext = () => {
    navigate(routes.clientAddress.path);
  };

  const onChangeSheetType = (selectedOption: Option) => {
    sheetType.setSelectedSheetType(selectedOption);
    getCalculatedPrice({ id_papel: selectedOption.value });
    getCalculatedShippingCost({ id_papel: selectedOption.value });
  };

  const onChangeSheetSize = (selectedOption: Option) => {
    sheetSize.setSelectedSheetSize(selectedOption);
    getCalculatedPrice({ id_hoja: selectedOption.value });
    getCalculatedShippingCost({ id_hoja: selectedOption.value });
  };

  const onChangeRingType = (selectedOption: Option) => {
    ringType.setSelectedRingType(selectedOption);
    getCalculatedPrice({ id_anillado: selectedOption.value });
    getCalculatedShippingCost({ id_anillado: selectedOption.value });
  };

  const onChangeColor = (selectedOption: Option) => {
    color.setSelectedColor(selectedOption);
    getCalculatedPrice({ id_color: selectedOption.value });
    getCalculatedShippingCost({ id_color: selectedOption.value });
  };

  const onChangeSide = (selectedOption: Option) => {
    side.setSelectedSide(selectedOption);
    getCalculatedPrice({ id_faz: selectedOption.value });
    getCalculatedShippingCost({ id_faz: selectedOption.value });
  };

  const onChangeCopies = (numberOfCopies: number) => {
    copies.setNumberOfCopies(numberOfCopies);
    getCalculatedPrice({ numberOfCopies });
    getCalculatedShippingCost({ numberOfCopies });
  };

  useEffect(() => {
    getEntities();
    // Detectar pestaña duplicada y generar nuevo tracking code
    handleTabDuplication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={isDesktopOrLaptop ? "new-order-desktop" : "new-order-mobile"}
    >
      <LogoSection />

      <div className="main-column">
        <div className="top-content">
          <div className="title-section">
            <Subheader>Paso 1</Subheader>

            <Title>Archivos y Opciones de Impresión</Title>

            <Text
              html="Tené en cuenta que las opciones elegidas se aplican a <b>todos los
          archivos</b> que cargues. En caso que quieras diferentes tipos de
          impresión, cargalo en órdenes separadas."
            />
          </div>

          <div className="file-section">
            <FileUpload />
          </div>

          <div className="notes-section">
            <Input
              title="Notas adicionales"
              multiline
              onChange={setAdditionalNotes}
              value={additionalNotes}
            />
          </div>
        </div>
        <div className="bottom-content">
          <div className="next-steps-section">
            <div className="step">
              <Subheader disabled>Paso 2</Subheader>
              <Subtitle disabled>Opciones de Envío</Subtitle>
            </div>

            <div className="step">
              <Subheader disabled>Paso 3</Subheader>
              <Subtitle disabled>Datos de Pago</Subtitle>
            </div>

            <div className="step">
              <Subheader disabled>Paso 4</Subheader>
              <Subtitle disabled>Confirmar y pagar la orden</Subtitle>
            </div>
          </div>
        </div>
      </div>

      <OrderSummary
        showAddressForm={false}
        showPaymentForm={false}
        isNextEnabled={isStep1Valid}
        onClickNext={onClickNext}
        orderDetailsForm={
          loading ? (
            <LoadingCards />
          ) : (
            <>
              <Dropdown
                title="Tamaño de hoja"
                className="margin-top"
                helpContent={
                  <>
                    <p>
                      <Text bold>Tamaño de hoja</Text> <br />
                      <Text>
                      Tenemos tres tamaños para que puedas imprimir: <br />
                      <b>A3</b> - mide 297 x 420 mm <br />
                      <b>A4</b> - mide 210 x 297 mm <br />
                      <b>Oficio</b> - mide 216 x 330 m <br />
                      En cualquiera de las tres medidas puede ser en forma Vertical u Horizontal
                      </Text>
                    </p>
                  </>
                }
                options={entitiesOptions.hoja}
                value={sheetSize?.selectedSheetSize?.value}
                onChange={onChangeSheetSize}
              />

              <Dropdown
                title="Tipo de impresión"
                className="margin-top"
                helpContent={
                  <>
                    <p>
                      <Text bold>Tipo de impresión</Text> <br />
                      <Text>
                        La impresión puede ser en <b>Blanco & Negro</b> o en <b>Color</b>.
                      </Text>
                    </p>
                  </>
                }
                options={entitiesOptions.color}
                value={color?.selectedColor?.value}
                onChange={onChangeColor}
              />

              <Dropdown
                title="Tipo de faz"
                className="margin-top"
                helpContent={
                  <>
                    <p>
                      <Text bold>Tipo de faz</Text> <br />
                      <Text>
                        La impresión puede ser <b>Simple Faz</b> o <b>Doble Faz</b>.
                      </Text>
                    </p>
                  </>
                }
                options={entitiesOptions.faz}
                value={side?.selectedSide?.value}
                onChange={onChangeSide}
              />

              <Dropdown
                title="Tipo de papel"
                className="margin-top"
                helpContent={
                  <>
                    <p>
                      <Text bold>Tipo de papel</Text> <br />
                      <Text>
                        Tenemos variedad en tipos de papel y gramajes. Algunos tipos de papel no tienen opción para la impresión en
                        doble faz.
                      </Text>
                    </p>
                  </>
                }
                options={entitiesOptions.papel}
                value={sheetType?.selectedSheetType?.value}
                onChange={onChangeSheetType}
              />

              <Dropdown
                title="Anillado"
                className="margin-top"
                options={entitiesOptions.anillado}
                value={ringType?.selectedRingType?.value}
                onChange={onChangeRingType}
                helpContent={
                  <>
                    <p>
                      <Text bold>¿Anillado largo o corto?</Text> <br />
                      <Text>
                        El anillado largo es el <b>anillado tradicional</b>, donde el
                        anillo atraviesa el lado largo de la hoja. El <b>anillado
                        corto</b> es el anillado donde el anillo atraviesa el lado
                        corto de la hoja.
                      </Text>
                    </p>

                    <p>
                      <Text bold>
                        ¿El anillado es por archivo o por pedido?
                      </Text>{" "}
                      <br />
                      <Text>
                        El anillado es <b>por pedido</b>, esto significa que si
                        seleccionas la opcion de anillado, todos los archivos
                        que subas seran anillados juntos y como un solo pedido.
                        Si deseas anillar solo algunos archivos, te recomendamos
                        que realices un pedido por separado para estos.
                      </Text>
                    </p>
                  </>
                }
              />

              <Input
                title="Copias"
                className="margin-top"
                helpContent={
                  <>
                    <p>
                      <Text bold>Copias</Text> <br />
                      <Text>
                        Acá podés poner la cantidad que necesitas.
                      </Text>
                    </p>
                  </>
                }
                type="number"
                value={copies.numberOfCopies}
                onChange={onChangeCopies}
              />
            </>
          )
        }
      />
    </div>
  );
};
